import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

interface HotelCity {
  Id?: number;
  id?: number;
  Name?: string;
  name?: string;
}

interface HotelDestinationAutocompleteProps {
  value: string;
  onChange: (name: string, id?: number) => void;
  placeholder?: string;
  label?: string;
}

const DEFAULT_POPULAR_CITIES = [
  { id: 4567, name: 'Dubai, UAE' },
  { id: 60763, name: 'London, United Kingdom' },
  { id: 148614, name: 'Mecca, Saudi Arabia' },
  { id: 148615, name: 'Medina, Saudi Arabia' },
  { id: 9821, name: 'Lagos, Nigeria' },
  { id: 11234, name: 'Abuja, Nigeria' },
  { id: 16786, name: 'Paris, France' },
  { id: 7645, name: 'New York, USA' },
  { id: 1823, name: 'Istanbul, Turkey' },
  { id: 2940, name: 'Cairo, Egypt' },
];

export default function HotelDestinationAutocomplete({
  value,
  onChange,
  placeholder = 'City, hotel, or landmark',
  label
}: HotelDestinationAutocompleteProps) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState<{ id?: number; name: string }[]>(DEFAULT_POPULAR_CITIES);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (!query || query.trim().length < 2) {
      setResults(DEFAULT_POPULAR_CITIES);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await axios.post('/api/travel/hotel-destinations', { text: query.trim(), query: query.trim() });
        const list = res.data?.results || [];
        if (Array.isArray(list) && list.length > 0) {
          setResults(list.map((c: HotelCity) => ({
            id: c.Id || c.id,
            name: c.Name || c.name || ''
          })));
        } else {
          const q = query.trim().toLowerCase();
          setResults(DEFAULT_POPULAR_CITIES.filter(c => c.name.toLowerCase().includes(q)));
        }
      } catch {
        const q = query.trim().toLowerCase();
        setResults(DEFAULT_POPULAR_CITIES.filter(c => c.name.toLowerCase().includes(q)));
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query, isOpen]);

  const handleSelect = (item: { id?: number; name: string }) => {
    setQuery(item.name);
    onChange(item.name, item.id);
    setIsOpen(false);
  };

  const handleInputChange = (text: string) => {
    setQuery(text);
    onChange(text);
    setIsOpen(true);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      {label && (
        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
          {label}
        </label>
      )}
      <div className="relative">
        <i className="ri-hotel-line absolute left-3.5 top-1/2 -translate-y-1/2 text-[#b8a080] text-lg pointer-events-none z-10"></i>
        <input
          type="text"
          value={query}
          onFocus={() => {
            setIsOpen(true);
            if (!query) setResults(DEFAULT_POPULAR_CITIES);
          }}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-white/10 hover:bg-white/15 focus:bg-white/20 border border-white/20 focus:border-[#b8a080] text-white placeholder-gray-400 text-sm font-medium rounded-xl pl-10 pr-4 py-3 outline-none transition-all duration-200 shadow-inner"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              onChange('');
              setResults(DEFAULT_POPULAR_CITIES);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-xs cursor-pointer"
          >
            <i className="ri-close-circle-fill text-base"></i>
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900/95 backdrop-blur-xl border border-white/15 rounded-xl shadow-2xl overflow-hidden z-50 max-h-64 overflow-y-auto divide-y divide-white/5">
          <div className="px-3 py-1.5 bg-black/40 text-[10px] uppercase font-bold text-gray-400 tracking-wider flex items-center justify-between">
            <span>{query ? 'Matching Destinations' : 'Popular Cities'}</span>
            <span className="text-[#b8a080]">Rigland Hotels</span>
          </div>
          {results.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-400">
              No matching destinations found. You can search directly with "{query}".
            </div>
          ) : (
            results.map((item, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSelect(item)}
                className="w-full text-left px-4 py-2.5 hover:bg-white/10 flex items-center gap-3 transition-colors cursor-pointer group"
              >
                <div className="w-8 h-8 rounded-lg bg-[#b8a080]/15 group-hover:bg-[#b8a080]/30 border border-[#b8a080]/30 flex items-center justify-center flex-shrink-0 transition-colors">
                  <i className="ri-map-pin-2-line text-[#b8a080] text-sm"></i>
                </div>
                <div className="min-w-0 flex-1">
                  <span className="font-semibold text-white text-sm truncate block">{item.name}</span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
